import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ScrollingText from './ScrollingText';

const UrlParamViewer = () => {
  const [searchParams] = useSearchParams();
  const [params, setParams] = useState({
    text: '',
    speed: 5,
    bgColor: '#2c3e50',
    bgAlpha: 1, // Default to fully opaque
    textColor: '#ffffff',
    scale: 1, // Default scale is 1 (100%)
    fontFamily: 'Roboto', // Default font
    weatherApiKey: '' // New parameter for weather API key
  });

  useEffect(() => {
    // Get parameters from URL
    const text = searchParams.get('text') || '';
    const speed = parseInt(searchParams.get('speed') || '5', 10);
    const bgColor = searchParams.get('bgColor') || '#2c3e50';
    const bgAlpha = parseFloat(searchParams.get('bgAlpha') || '1.0');
    const textColor = searchParams.get('textColor') || '#ffffff';
    const scale = parseFloat(searchParams.get('scale') || '1.0');
    const fontFamily = searchParams.get('fontFamily') || 'Roboto';
    const weatherApiKey = searchParams.get('weatherApiKey') || '';

    // Update state with URL parameters
    setParams({
      text: decodeURIComponent(text),
      speed: Math.min(Math.max(speed, 1), 10), // Ensure speed is between 1-10
      bgColor,
      bgAlpha: Math.min(Math.max(bgAlpha, 0), 1), // Ensure alpha is between 0-1
      textColor,
      scale: Math.min(Math.max(scale, 0.5), 5), // Limit scale between 0.5 and 5 (50% to 500%)
      fontFamily,
      weatherApiKey
    });
    
    // Make the body transparent for overlay purposes
    document.body.style.backgroundColor = 'transparent';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    
    // Set the HTML background to transparent as well
    document.documentElement.style.backgroundColor = 'transparent';
    
    return () => {
      // Reset body styles when component unmounts
      document.body.style.backgroundColor = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [searchParams]);

  // Process the background color with alpha
  const processedBgColor = (() => {
    // If the color is in hex format (#RRGGBB), convert to rgba
    if (params.bgColor.startsWith('#')) {
      const r = parseInt(params.bgColor.slice(1, 3), 16);
      const g = parseInt(params.bgColor.slice(3, 5), 16);
      const b = parseInt(params.bgColor.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${params.bgAlpha})`;
    }
    // If it's a named color, also convert to rgba
    else if (params.bgColor.match(/^[a-z]+$/i)) {
      // Create a temporary element to get the RGB values
      const elem = document.createElement('div');
      elem.style.color = params.bgColor;
      document.body.appendChild(elem);
      const computedColor = window.getComputedStyle(elem).color;
      document.body.removeChild(elem);
      
      // Extract RGB values and add alpha
      const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        const [, r, g, b] = rgbMatch;
        return `rgba(${r}, ${g}, ${b}, ${params.bgAlpha})`;
      }
    }
    
    // Fallback to the original color
    return params.bgColor;
  })();

  // Minimal clean view with just the scrolling text and transparent background
  return (
    <div className="clean-view" style={{ height: '100vh', width: '100vw', background: 'transparent' }}>
      {params.text ? (
        <ScrollingText 
          content={params.text}
          speed={params.speed}
          backgroundColor={processedBgColor}
          textColor={params.textColor}
          scale={params.scale}
          fontFamily={params.fontFamily}
          weatherApiKey={params.weatherApiKey}
        />
      ) : null}
    </div>
  );
};

export default UrlParamViewer; 